import React, {useEffect, useMemo, useRef, useState} from 'react';
import classNames from 'classnames';
import {useDisclosure} from 'hooks/useDisclosure';
import {OptionType} from 'interfaces';
import {useTranslation} from 'react-i18next';

import {Button} from '../Button';
import {ControlledCheckbox} from '../Checkbox/ControlledCheckbox';
import {DropdownContainer} from '../Form/Select/DetachedSearchSelect/DropdownContainer';
import {SearchIcon} from '../SvgIcon';

import {ButtonDropdown} from './ButtonDropdown';
import {dropdownStyle} from './dropdown-style';

interface IndeterminateComboBoxProps {
  label: string;
  options: OptionType[];
  onChange: (selected: OptionType[]) => void;
  placeholder?: string;
  selectedAllLabel: string;
  partlySelectedLabel: string;
  className?: string;
  value?: OptionType[];
}

export const IndeterminateComboBox: React.FC<IndeterminateComboBoxProps> = ({
  label,
  options,
  onChange,
  placeholder,
  selectedAllLabel,
  partlySelectedLabel,
  className,
  value,
}) => {
  const {isOpen, close, toggle} = useDisclosure();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<OptionType[]>([]);

  const outerRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLButtonElement>(null);
  const {t} = useTranslation();

  const currentValue = value ? value : selected;

  useEffect(() => {
    if (value) {
      setSelected(value);
    }
  }, [value]);

  const filteredOptions = useMemo(() => {
    return options.filter(opt =>
      opt.label.toLowerCase().includes(search.toLowerCase()),
    );
  }, [search, options]);

  const allSelected =
    currentValue.length === options.length && options.length > 0;
  const someSelected = currentValue.length > 0 && !allSelected;

  const handleSelectAll = (checked: boolean) => {
    const newSelected = checked ? options : [];
    if (!value) {
      setSelected(newSelected);
    }
    onChange(newSelected);
  };

  const handleSelect = (option: OptionType, checked: boolean) => {
    const newSelected = checked
      ? [...currentValue, option]
      : currentValue.filter(o => o.value !== option.value);

    if (!value) {
      setSelected(newSelected);
    }
    onChange(newSelected);
  };

  const handleApply = () => {
    close();
  };

  const selectedOutput = useMemo(() => {
    if (currentValue.length === 0) {
      return placeholder;
    }

    if (currentValue.length === options.length) {
      return selectedAllLabel;
    }

    return `${partlySelectedLabel} (${currentValue.length})`;
  }, [currentValue, options, placeholder, partlySelectedLabel]);

  return (
    <div
      className={classNames('relative h-auto isolate w-full', className)}
      ref={outerRef}
    >
      <DropdownContainer
        isOpen={isOpen}
        onClose={close}
        target={
          <ButtonDropdown
            ref={ref}
            onClick={toggle}
            label={label}
            selectedOutput={selectedOutput ?? placeholder ?? ''}
            isOpen={isOpen}
          />
        }
        styles={{
          dropdownMenu: () => ({...dropdownStyle, position: 'fixed'}),
        }}
      >
        <div
          style={{width: ref.current?.clientWidth}}
          className={classNames(
            `z-10 bg-white rounded-2xl shadow-md flex flex-col cursor-pointer`,
          )}
        >
          <div className="relative p-3">
            <input
              type="text"
              placeholder={t('singleSession.search_placeholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm border-gray-400 h-10"
            />
            <div className="absolute right-6 top-1/2 -translate-y-1/2">
              <SearchIcon fillColor="text-gray-800" strokeWidth={4} />
            </div>
          </div>
          <div className="px-4 py-2">
            <ControlledCheckbox
              label={selectedAllLabel}
              indeterminate={someSelected}
              checked={allSelected}
              onChange={handleSelectAll}
              className="pt-2"
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.map(option => (
              <div className="px-4 py-2 ml-4" key={option.value}>
                <ControlledCheckbox
                  label={option.label}
                  checked={currentValue.some(o => o.value === option.value)}
                  onChange={checked => handleSelect(option, checked)}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 p-3 border-t">
            <Button
              btnType="custom"
              bgColor="none"
              borderRadius="full"
              borderColor="gray"
              textColor="gray"
              className="px-4 py-2 border text-gray-700"
              onClick={close}
            >
              {t('singleSession.filter.close')}
            </Button>
            <Button
              btnType="custom"
              borderRadius="full"
              bgColor="blue"
              className=" px-4 py-2 bg-blue-600 text-white"
              onClick={handleApply}
            >
              {t('singleSession.filter.apply')}
            </Button>
          </div>
        </div>
      </DropdownContainer>
    </div>
  );
};
